import moment, { Moment, MomentInput } from 'moment';
import momentTz, { Moment as MomentTz, MomentInput as MomentTzInput } from 'moment-timezone';
import { getTimeFormat, getTimeFormatsByLength } from './timeRegex';
import {
  NewTimeParams,
  MomentIsNow,
  GetDateUnitCallback,
  SetDateUnitCallback,
  ModifyDateUnitCallback,
} from './dateTypes';

/**
 * Hour constant of time.
 */
export const HOUR = 'hours';

/**
 * Minute constant of time.
 */
export const MINUTE = 'minutes';

/**
 * Second constant of time.
 */
export const SECOND = 'second';

/**
 * AM time period constant of time.
 */
export const AM = 'am';

/**
 * PM time period constant of time.
 */
export const PM = 'pm';

/**
 * Ensure Moment TZ
 * Helper function to convert base Moment to Moment-TZ
 * @deprecated
 */
export const ensureMomentTz = (time: Moment): MomentTz => momentTz(time as MomentTzInput);

/**
 * **isKeyAllowed**_(key)_<br>
 * Helper function when inputting time to check if key in allowed.
 * @param { String } key: The key to check.
 * @returns { Boolean } If the key is allowed for input.
 */
export const isKeyAllowed = (key = '') => {
  const keysAllowed =
    /^(shift)$|^(arrowleft)$|^(arrowright)$|^(left)$|^(right)$|^(tab)$|^[0-9apm:\s]$/g;
  return keysAllowed.test(key.toLowerCase());
};

/**
 * **getNewTime**_(timeOptions)_<br>
 * Get current time object.
 * @param { Object } timeOptions: Optional opject containing the following options:<br>
 *  {<br>
 *    { Moment } time: Moment date object to create new time from.<br>
 *    { Boolean } isNow: Adds isNow flag to keep it up to time with current time.<br>
 *    { String } timeZone: TimeZone to add to time.<br>
 *  }<br>
 * @returns { Moment } Current time moment object.
 * @deprecated use `getNewDate` from `utils/datetime`
 */
export const getNewTime = ({ time, isNow, timeZone }: NewTimeParams = {}) => {
  let newTime: MomentIsNow = moment(time);
  if (isNow) {
    newTime = moment(time);
    newTime.isNow = true;
  } else {
    newTime = moment().startOf('day');
  }
  if (timeZone) {
    newTime = ensureMomentTz(newTime);
    (newTime as MomentTz)?.tz(timeZone);
  }
  return newTime;
};

/**
 * **getHourTimePeriod**_(hour)_<br>
 * Gets the time periods of a given hour.
 * @param { Number } hour: The hour to determine the time period.
 * @returns { String } AM or PM.
 * @deprecated Use `isMorning`/`isEvening` from `utils/datetime`
 */
export const getHourTimePeriod = (hour: number) => (hour < 12 ? AM : PM);

/**
 * displayTimeUnit**_(time, unit, twentyFourHour)_<br>
 * From a given time object, display given time unit.
 * @param { Moment } time: Moment time object to get time unit from.
 * @param { String } unit: Unit to display.
 * @param { Boolean } twentyFourHour: Formats the time to 24-Hour clock.
 * @returns { String } The time unit of the given time.
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const displayTimeUnit = (time?: MomentInput, unit = HOUR, twentyFourHour = false) => {
  let timeUnit = '';
  if (time) {
    let format = ' ';
    switch (unit.toLowerCase()) {
      case HOUR:
        format = twentyFourHour ? 'HH' : 'hh';
        break;
      case MINUTE:
        format = 'mm';
        break;
      case SECOND:
        format = 'ss';
        break;
      default:
        break;
    }
    timeUnit = moment(time).format(format);
  }
  return timeUnit;
};

/**
 * **getTimeFormatString**_(twentyFourHour, displaySeconds)_<br>
 * Gets the time format string.
 * @param { Boolean } twentyFourHour: Formats the time to 24-Hour clock.
 * @param { Boolean } displaySeconds: Shows seconds in time.
 * @param { Boolean } displayTimeZone: Shows timezone string.
 * @returns { String } The time format string.
 * @deprecated
 */
export const getTimeFormatString = (
  twentyFourHour = false,
  displaySeconds = false,
  displayTimeZone = false,
) => {
  const dayDivision = twentyFourHour ? '' : ' A';
  const hourMinutes = twentyFourHour ? 'HH:mm' : 'hh:mm';
  const seconds = displaySeconds ? ':ss' : '';
  const timeZone = displayTimeZone ? ' z' : '';
  return `${hourMinutes}${seconds}${dayDivision}${timeZone}`;
};

/**
 * **displayTime**_(time, twentyFourHour, displaySeconds)_<br>
 * Formats time to readable format.
 * @param { Moment } time: Moment time object to format.
 * @param { Boolean } twentyFourHour: Formats the time to 24-Hour clock.
 * @param { Boolean } displaySeconds: Shows seconds in time.
 * @returns { String } The formatted time to display.
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const displayTime = (
  time: MomentInput,
  twentyFourHour = false,
  displaySeconds = false,
  timeZone = null,
) => {
  const format = getTimeFormatString(twentyFourHour, displaySeconds, Boolean(timeZone));
  return timeZone ? momentTz.tz(time, timeZone).format(format) : moment(time).format(format);
};

/**
 * **setTimeUnit**_(time, amount, unit)_<br>
 * Set the value of a time unit.
 * @param { Moment } time: Moment time object to set value of.
 * @param { Number } amount: Amount to set.
 * @param { String } unit: Unit to set.
 * @returns { Moment } updated time.
 * @deprecated Use `setHours`, `setMinutes`, or `setSeconds` from `utils/datetime`
 */
export const setTimeUnit = (
  time?: MomentInput,
  amount?: string | number,
  unit: keyof Moment = HOUR,
) => {
  const realAmount =
    typeof amount !== 'undefined' && typeof amount === 'string' ? parseInt(amount, 10) : amount;

  return (moment(time)[unit] as SetDateUnitCallback)(realAmount);
};

/**
 * setTimeOfDay**_(time, am)_<br>
 * Sets time to am or pm.
 * @param { Moment } time: Moment time object to set time of day.
 * @param { Boolean } am: true: set time to am. false: set time to pm.
 * @returns { Moment } updated time.
 * @deprecated
 */
const setTimeOfDay = (time: MomentInput, am: boolean) => {
  const currentHour = moment(time).hours();
  if (currentHour > 11 && am) {
    return moment(time).subtract(12, HOUR);
  } else if (currentHour <= 11 && !am) {
    return moment(time).add(12, HOUR);
  }
  return moment(time);
};

/**
 * **setMorning**_(time)_<br>
 * Sets time to am.
 * @param { Moment } time: Moment time object to set.
 * @returns { Moment } updated time.
 * @deprecated Use `setMorning` from `utils/datetime`
 */
export const setMorning = (time?: MomentInput) => setTimeOfDay(time, true);

/**
 * **setEvening**_(time)_<br>
 * Sets time to pm.
 * @param { Moment } time: Moment time object to set.
 * @returns { Moment } updated time.
 * @deprecated Use `setEvening` from `utils/datetime`
 */
export const setEvening = (time?: MomentInput) => setTimeOfDay(time, false);

/**
 * **setTime**_(currentTime, newTime, twentyFourHour, displaySeconds)_<br>
 * Parses a given string or time and sets it to current time.
 * @param { Moment } currentTime: The current Moment time object.
 * @param { String } newTime: Formatted string of new time to set.
 * @param { Boolean } twentyFourHour: Formats the time to 24-Hour clock.
 * @param { Boolean } displaySeconds: Shows seconds in time.
 * @returns { Moment } updated time.
 * @deprecated Use `setTime` from `utils/datetime`
 */
export const setTime = (
  currentTime: Moment,
  newTime: string,
  twentyFourHour = false,
  displaySeconds = false,
) => {
  let changedTime = currentTime;

  if (newTime) {
    const timeArray = newTime.replace(' ', ':').split(':');
    const arrayLength = timeArray.length;
    const hourOfDay = parseInt(timeArray[0], 10);

    if (arrayLength > 1) {
      changedTime = moment(currentTime).hours(hourOfDay).minutes(parseInt(timeArray[1], 10));

      if (displaySeconds && arrayLength > 2) {
        changedTime = changedTime.seconds(parseInt(timeArray[2], 10));
      }

      if (!twentyFourHour) {
        let timePeriod = displaySeconds ? timeArray[3] : timeArray[2];
        if (!timePeriod) {
          timePeriod = getHourTimePeriod(hourOfDay);
        }
        changedTime = PM.includes(timePeriod.toLowerCase())
          ? setEvening(changedTime)
          : setMorning(changedTime);
      }
    }
  }
  return changedTime;
};

/**
 * **formatTime**_(input, twentyFourHour, hasSeconds)_<br>
 * From a given input, format to readable time.
 * @param { String } input: Time input to format.
 * @param { Boolean } twentyFourHour: Formats the unit to 24-Hour clock.
 * @param { Boolean } hasSeconds: Input time includes seconds.
 * @returns { Object | String } The formatted time.
 * @deprecated Use `localizeDateTime` from `utils/l10n`
 */
export const formatTime = (input: string, twentyFourHour = false, hasSeconds = false) => {
  let output = input.trim();

  const { formatString } = getTimeFormat(output, getTimeFormatsByLength(output.length, hasSeconds));
  if (formatString) {
    output = displayTime(moment(output, formatString), twentyFourHour, hasSeconds);
  } else if (input === '') {
    output = '';
  } else {
    output = 'invalid';
  }
  return output;
};

/**
 * **formatTimeForSave**_(time)_<br>
 * Parses time to save format.
 * @param { moment } time: The moment time object to format to save format.
 * @returns { Number } The parsed time to save format.
 * @deprecated Use `convertToString` from `utils/datetime`
 */
export const formatTimeForSave = (time?: MomentInput) => {
  if (time) {
    return moment(time).toISOString();
  }
  return null;
};

/**
 * **parseTimeFromSave**_(time)_<br>
 * Parses time from save format.
 * @param { string } time: The string save time format to moment.
 * @returns { Number } The parsed time string to moment.
 * @deprecated Use `getNewDate` from `utils/datetime`
 */
export const parseTimeFromSave = (time?: string | null) => {
  if (time) {
    return moment(time, moment.ISO_8601);
  }
  return null;
};

/**
 * **modifyTimeUnit**_(time, add, amount, unit, modifyTime)_<br>
 * Adds or subtracts from time based on
 * @param { Moment } time: Moment time object to add/subtract from.
 * @param { Boolean } add: true: Add to time. false: subtract from time.
 * @param { Number } amount: Amount of units to add/subtract from time.
 * @param { String } unit: Unit to add/subtract from time.
 * @param { Boolean } modifyTime: Add or subtract from time to affect all
 *                                 Set false to only change given unit.
 * @returns { Moment } Updated time.
 * @deprecated Use `modifyTimeUnit` from `utils/datetime`
 */
export const modifyTimeUnit = (
  time?: MomentInput,
  add = true,
  amount = 1,
  unit: keyof Moment = HOUR,
  modifyTime = false,
) => {
  if (modifyTime) {
    return (moment(time)[add ? 'add' : 'subtract'] as ModifyDateUnitCallback)(amount, unit);
  }

  // Calculate the new value of unit to set to avoid changing other unit values.
  const deltaAmount = (add ? 1 : -1) * amount;
  const currentAmount = (moment(time)[unit] as GetDateUnitCallback)();
  const maxOfUnit = unit === HOUR ? 23 : 59;
  let amountToAdd = currentAmount + deltaAmount;

  if (amountToAdd < 0) {
    amountToAdd = maxOfUnit - (amount - 1);
  } else if (amountToAdd > maxOfUnit) {
    amountToAdd = 0;
  }
  return setTimeUnit(time, amountToAdd, unit);
};

/**
 * **stripLeadingZero**_(time)_<br>
 * Removes leading zero from hours place of time stamp
 * @param { String } time: Display time string of format HH:mm
 * @returns {String } Time string without a leading zero in the hours place
 * @deprecated
 */
export const stripLeadingZero = (time: string) => (time.charAt(0) === '0' ? time.slice(1) : time);

/**
 * to12HourClock
 * Converts 24-hour time to 12-hour format
 *
 * @param { String } timeIn24HourClock Time string in 24-hour format 'HH:mm' or 'HH:mm:...'
 * @param { Boolean } shouldUseUppercase Whether to use uppercase meridiem format (ex: `AM` or `a.m.`)
 * @returns { String } Time string in 12-hour format
 * @deprecated
 */
export const to12HourClock = (timeIn24HourClock: string, shouldUseUppercase = false) => {
  const [hoursStr, minutesStr] = timeIn24HourClock.split(':');

  const hourIn24 = parseInt(hoursStr, 10);
  const hourIn12 = ((hourIn24 + 11) % 12) + 1;

  const meridiemMapper = { true: { am: 'AM', pm: 'PM' }, false: { am: 'a.m.', pm: 'p.m.' } };
  const meridiem =
    hourIn24 < 12 || hourIn24 === 24
      ? meridiemMapper[`${shouldUseUppercase}`].am
      : meridiemMapper[`${shouldUseUppercase}`].pm;

  return `${hourIn12}:${minutesStr} ${meridiem}`;
};

/**
 * Formats time received from backend to a preserve only hours and
 * minutes in a 24-hour format by excluding trailing numbers.
 *
 * Example: "05:30:00.000" => "05:30"
 * @deprecated
 */
export const trim24HourClockTime = (time: string) => time.slice(0, 5);
